import { useEffect, useState, useCallback, useMemo } from "react";
import { Table, Dropdown, Button, Tooltip, OverlayTrigger, Alert } from "react-bootstrap";
import { useLocation, useHistory } from 'react-router-dom';
import { MdOutlineArrowBack } from "react-icons/md";
import { IoIosWarning } from "react-icons/io";
import { FiInfo } from "react-icons/fi";
import RelatorioApiService from '../../../services/api/RelatorioApiService';
import FormatDataService from "../../../services/util/FormatData";
import UtilService from "../../../services/util";
import PageContainer from "../../../components/PageContainer";
import Loading from "../../../components/Loading";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";


function RelatorioRelacaoHorasView() {
    const location = useLocation();
    const history = useHistory();
    const { data } = location.state || { data: { periodoInicial: "", periodoFinal: "" } };
    const { periodoInicial, periodoFinal } = data;

    const [apontamentos, setApontamentos] = useState([]);
    const [dadosOriginais, setDadosOriginais] = useState([]); // Dados originais da API para exportação
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [selectedGestor, setSelectedGestor] = useState('');
    const [selectedColaborador, setSelectedColaborador] = useState('');
    const [sortConfig, setSortConfig] = useState({ key: 'nomeCompleto', direction: 'asc' });

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            setError(null);
            try {
                const dataInicialFormatada = FormatDataService.formatarDataParaYYYYMMDD(periodoInicial);
                const dataFinalFormatada = FormatDataService.formatarDataParaYYYYMMDD(periodoFinal);
                const response = await RelatorioApiService.getRelatorioRelacaoHoras(dataInicialFormatada, dataFinalFormatada);
                const rawData = response.data.data || [];
                
                // Armazenar dados originais para exportação
                setDadosOriginais(rawData);
                
                // Função para extrair número da data para ordenação
                const extrairNumeroDia = (diaString) => {
                    const match = diaString.match(/(\d+)/);
                    return match ? parseInt(match[1], 10) : 0;
                };

                // Transforma dados em formato tabular
                const tabelarData = [];
                rawData.forEach(colab => {
                    const dias = colab.attendances ?? [];
                    dias.forEach(dia => {
                        tabelarData.push({
                            id: `${colab.FullName}-${dia.dia}`,
                            gestor: colab.ManagerFullName || "Sem Gestor",
                            nomeCompleto: colab.FullName || "N/D",
                            dia: dia.dia || "",
                            diaOrdenacao: extrairNumeroDia(dia.dia || ""),
                            entrada: dia.entrada ?? "00:00",
                            pausa: dia.pausa ?? "00:00",
                            retorno: dia.retorno ?? "00:00",
                            saida: dia.saida ?? "00:00",
                            duracao: dia.duracao ?? "00:00",
                            extra: dia.extra ?? "00:00",
                            extraExtraordinario: dia.extraExtraordinario ?? "00:00",
                            adicionalNoturno: dia.adicionalNoturno ?? "00:00"
                        });
                    });
                });
                
                setApontamentos(tabelarData);
            } catch (err) {
                console.error('Erro ao carregar dados do relatório:', err);
                setError('Erro ao carregar dados do relatório. Tente novamente.');
            } finally {
                setLoading(false);
            }
        };

        if (periodoInicial && periodoFinal) {
            fetchData();
        }
    }, [periodoInicial, periodoFinal]);


    const exportDataInExcel = async () => {
        if (!Array.isArray(dadosOriginais) || dadosOriginais.length === 0) {
            alert("Erro: dados inválidos para exportação.");
            return;
        }

        // Determinar se deve mostrar as colunas de aprovação (mesma regra do tooltip "aprovadas pelo DP")
        const hoje = new Date();
        const diaAtual = hoje.getDate();
        const mesAtual = hoje.getMonth(); // 0-11
        const anoAtual = hoje.getFullYear();

        const { mesRelatorio, anoRelatorio } = extrairMesAnoRelatorio();
        const diferencaMeses = (anoAtual - anoRelatorio) * 12 + (mesAtual - mesRelatorio);

        let mostrarColunasAprovacao = false; // Por padrão, oculta

        if (diaAtual >= 1 && diaAtual <= 15) {
            // Dias 1 a 15 do mês atual
            if (diferencaMeses >= 2) {
                // Relatórios de meses até (mês atual - 2) ou anterior → OCULTAR
                mostrarColunasAprovacao = false;
            } else {
                // Relatórios recentes (mês atual - 1 ou mês atual) → MOSTRAR
                mostrarColunasAprovacao = true;
            }
        } else {
            // Dia 16 ou mais do mês atual
            if (diferencaMeses >= 2) {
                // Relatórios de meses até (mês atual - 1) ou anterior → OCULTAR
                mostrarColunasAprovacao = false;
            } else {
                // Relatórios do mês atual → MOSTRAR
                mostrarColunasAprovacao = true;
            }
        }

        const workbook = new ExcelJS.Workbook();

        const toExcelTime = (hhmm) => {
            if (!hhmm || typeof hhmm !== "string" || !hhmm.includes(":")) return 0;
            const [h, m] = hhmm.split(":").map(Number);
            if (Number.isNaN(h) || Number.isNaN(m)) return 0;
            return (h * 60 + m) / 1440;
        };

        const greenFill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "FFCCFFCC" }
        };

        const totalFill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "FFFFFF00" }
        };

        const borderStyle = {
            top: { style: "thin", color: { argb: "FFAAAAAA" } },
            left: { style: "thin", color: { argb: "FFAAAAAA" } },
            bottom: { style: "thin", color: { argb: "FFAAAAAA" } },
            right: { style: "thin", color: { argb: "FFAAAAAA" } }
        };

        const validationOptions =
            '"Aprovado como Hora extra,Aprovado como Hora extra Extraordinaria,Aprovado como adicional noturno,Banco de horas,Hora extra parcial"';

        const gestoresMap = new Map();

        dadosOriginais.forEach(colab => {
            const gestor = colab.ManagerFullName || "Sem Gestor";
            if (!gestoresMap.has(gestor)) {
                gestoresMap.set(gestor, []);
            }
            gestoresMap.get(gestor).push(colab);
        });

        gestoresMap.forEach((colaboradores, gestorNome) => {
            const safeSheetName = gestorNome
                .substring(0, 31)
                .replace(/[\\/*?:[\]]/g, "");

            const worksheet = workbook.addWorksheet(safeSheetName);

            colaboradores.forEach((colab) => {
                const nome = colab.FullName || "N/D";
                const dias = colab.attendances ?? [];

                let maxExtras = 0;
                dias.forEach((dia) => {
                    Object.keys(dia || {}).forEach((k) => {
                        const m = k.match(/^pausa_(\d+)$/);
                        if (m) maxExtras = Math.max(maxExtras, Number(m[1]));
                    });
                });

                const header = [
                    "Dia",
                    "Nome",
                    "Entrada",
                    "Pausa",
                    "Retorno"
                ];

                for (let i = 2; i <= maxExtras; i++) {
                    header.push(`Pausa ${i}`, `Retorno ${i}`);
                }

                header.push(
                    "Saída",
                    "Duração",
                    "Hora Extra",
                    "Hora Extra Extraordinária",
                    "Adicional Noturno"
                );

                // Adicionar colunas de aprovação apenas se o relatório estiver "aprovado pelo DP"
                if (mostrarColunasAprovacao) {
                    header.push(
                    "Situação",
                    "Projeto/Cliente",
                    "Simulação automática HE",
                    "Valor Manual (se parcial)",
                    "Observações"
                );
                }

                const headerRow = worksheet.addRow(header);

                headerRow.eachCell((cell) => {
                    cell.font = { bold: true };
                    cell.fill = greenFill;
                    cell.border = borderStyle;
                    cell.alignment = { horizontal: "center" };
                });

                const colDuracao = header.indexOf("Duração") + 1;
                const colExtra = header.indexOf("Hora Extra") + 1;
                const colExtraEx = header.indexOf("Hora Extra Extraordinária") + 1;
                const colNoturno = header.indexOf("Adicional Noturno") + 1;
                
                // Colunas de aprovação (só existem se mostrarColunasAprovacao for true)
                const colSituacao = mostrarColunasAprovacao ? header.indexOf("Situação") + 1 : -1;
                const colProjetocliente = mostrarColunasAprovacao ? header.indexOf("Projeto/Cliente") + 1 : -1;
                const colSimulacao = mostrarColunasAprovacao ? header.indexOf("Simulação automática HE") + 1 : -1;
                const colManual = mostrarColunasAprovacao ? header.indexOf("Valor Manual (se parcial)") + 1 : -1;

                const greenColumns = [
                    colDuracao,
                    colExtra,
                    colExtraEx,
                    colNoturno
                ];

                const firstRow = headerRow.number + 1;

                dias.forEach((dia) => {
                    const rowData = [
                        dia.dia ?? "",
                        nome,
                        dia.entrada ?? "00:00",
                        dia.pausa ?? "00:00",
                        dia.retorno ?? "00:00",
                    ];

                    for (let i = 2; i <= maxExtras; i++) {
                        rowData.push(dia[`pausa_${i}`] ?? "", dia[`retorno_${i}`] ?? "");
                    }

                    rowData.push(
                        dia.saida ?? "00:00",
                        "", // Duração
                        "", // Hora Extra
                        "", // Hora Extra Extraordinária
                        ""  // Adicional Noturno
                    );

                    // Adicionar células vazias para as colunas de aprovação se elas existirem
                    if (mostrarColunasAprovacao) {
                        rowData.push(
                            "", // Situação
                            "", // Projeto/Cliente
                            "", // Simulação automática HE
                            "", // Valor Manual (se parcial)
                            ""  // Observações
                    );
                    }

                    const row = worksheet.addRow(rowData);
                    const r = row.number;

                    const durCell = worksheet.getCell(r, colDuracao);
                    durCell.value = toExcelTime(dia.duracao ?? "00:00");
                    durCell.numFmt = "[h]:mm";

                    const extraCell = worksheet.getCell(r, colExtra);
                    extraCell.value = toExcelTime(dia.extra ?? "00:00");
                    extraCell.numFmt = "[h]:mm";

                    const extraExCell = worksheet.getCell(r, colExtraEx);
                    extraExCell.value = toExcelTime(dia.extraExtraordinario ?? "00:00");
                    extraExCell.numFmt = "[h]:mm";

                    const noturnoCell = worksheet.getCell(r, colNoturno);
                    noturnoCell.value = toExcelTime(dia.adicionalNoturno ?? "00:00");
                    noturnoCell.numFmt = "[h]:mm";

                    // Preencher colunas de aprovação apenas se elas existirem
                    if (mostrarColunasAprovacao) {
                    const manualCell = worksheet.getCell(r, colManual);
                    manualCell.numFmt = "[h]:mm";

                    const situacaoCell = worksheet.getCell(r, colSituacao);
                    situacaoCell.dataValidation = {
                        type: "list",
                        allowBlank: true,
                        formulae: [validationOptions]
                    };

                    const simulacaoCell = worksheet.getCell(r, colSimulacao);
                    simulacaoCell.value = {
                        formula: `
                            (
                                IF(${situacaoCell.address}="Aprovado como Hora extra", ${extraCell.address},
                                    IF(${situacaoCell.address}="Aprovado como Hora extra Extraordinaria", ${extraExCell.address},
                                        IF(${situacaoCell.address}="Aprovado como adicional noturno", ${noturnoCell.address}, 0)
                                    )
                                )
                            ) + IF(${manualCell.address}="", 0, ${manualCell.address})
                        `.replace(/\s+/g, " ")
                    };
                    simulacaoCell.numFmt = "[h]:mm";
                    }

                    row.eachCell((cell, colNumber) => {
                        cell.border = borderStyle;
                        cell.alignment = { horizontal: "center" };
                        if (greenColumns.includes(colNumber)) {
                            cell.fill = greenFill;
                        }
                    });
                });

                const lastRow = worksheet.lastRow.number;

                const totalRow = worksheet.addRow(
                    header.map((h, idx) => {
                        if (idx === 0) return "Total";
                        if (h === "Duração") return { formula: `SUM(${worksheet.getCell(firstRow, colDuracao).address}:${worksheet.getCell(lastRow, colDuracao).address})` };
                        if (h === "Hora Extra") return { formula: `SUM(${worksheet.getCell(firstRow, colExtra).address}:${worksheet.getCell(lastRow, colExtra).address})` };
                        if (h === "Hora Extra Extraordinária") return { formula: `SUM(${worksheet.getCell(firstRow, colExtraEx).address}:${worksheet.getCell(lastRow, colExtraEx).address})` };
                        if (h === "Adicional Noturno") return { formula: `SUM(${worksheet.getCell(firstRow, colNoturno).address}:${worksheet.getCell(lastRow, colNoturno).address})` };
                        if (mostrarColunasAprovacao && h === "Simulação automática HE") return { formula: `SUM(${worksheet.getCell(firstRow, colSimulacao).address}:${worksheet.getCell(lastRow, colSimulacao).address})` };
                        if (mostrarColunasAprovacao && h === "Valor Manual (se parcial)") return { formula: `SUM(${worksheet.getCell(firstRow, colManual).address}:${worksheet.getCell(lastRow, colManual).address})` };
                        return "";
                    })
                );

                totalRow.eachCell((cell, colNumber) => {
                    cell.font = { bold: true };
                    cell.border = borderStyle;
                    cell.alignment = { horizontal: "center" };
                    cell.fill = greenColumns.includes(colNumber) ? greenFill : totalFill;
                });

                const colunasComFormatoHora = [
                    colDuracao,
                    colExtra,
                    colExtraEx,
                    colNoturno
                ];

                // Adicionar colunas de aprovação apenas se elas existirem
                if (mostrarColunasAprovacao) {
                    colunasComFormatoHora.push(colSimulacao, colManual);
                }

                colunasComFormatoHora.forEach((c) => {
                    if (c > 0) {
                    worksheet.getCell(totalRow.number, c).numFmt = "[h]:mm";
                    }
                });

                // Adicionar linha com Hora Extra 50% como um total separado
                const horaExtra50Row = worksheet.addRow(
                    header.map((h, idx) => {
                        if (idx === 0) return "Hora Extra 50%";
                        return "";
                    })
                );
                horaExtra50Row.getCell(colExtra).value = colab.horaExtra50 ? toExcelTime(colab.horaExtra50) : "";
                horaExtra50Row.getCell(colExtra).numFmt = "[h]:mm";

                horaExtra50Row.eachCell((cell, colNumber) => {
                    cell.font = { bold: true };
                    cell.border = borderStyle;
                    cell.alignment = { horizontal: "center" };
                    cell.fill = greenColumns.includes(colNumber) ? greenFill : totalFill;
                });

                // Adicionar linha com Incremento no Banco no Mês (Total Hora Extra - Hora Extra 50%)
                const incrementoRow = worksheet.addRow(
                    header.map((h, idx) => {
                        if (idx === 0) return "Incremento no Banco no Mês:";
                        if (h === "Hora Extra") return { formula: `${totalRow.getCell(colExtra).address}-${horaExtra50Row.getCell(colExtra).address}` };
                        return "";
                    })
                );
                incrementoRow.getCell(colExtra).numFmt = "[h]:mm";

                incrementoRow.eachCell((cell, colNumber) => {
                    cell.font = { bold: true };
                    cell.border = borderStyle;
                    cell.alignment = { horizontal: "center" };
                    cell.fill = greenColumns.includes(colNumber) ? greenFill : totalFill;
                });

                worksheet.addRow([]);
                worksheet.addRow([]);
            });

            worksheet.columns.forEach(col => {
                let max = 12;
                col.eachCell(cell => {
                    if (cell.value) {
                        const len = cell.value.toString().length;
                        if (len > max) max = len;
                    }
                });
                col.width = max + 2;
            });
        });

        const nomeArquivo =
            `Relatorio_Relacao_de_Horas_${FormatDataService.formatarDataParaYYYYMMDD(periodoInicial)}_a_${FormatDataService.formatarDataParaYYYYMMDD(periodoFinal)}.xlsx`;

        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(
            new Blob([buffer], {
                type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            }),
            nomeArquivo
        );
    };

    // Converter string HH:MM para minutos
    const timeStringToMinutes = useCallback((timeString) => {
        if (!timeString || typeof timeString !== 'string') return 0;
        const parts = timeString.split(':');
        if (parts.length !== 2) return 0;
        const hours = parseInt(parts[0], 10) || 0;
        const minutes = parseInt(parts[1], 10) || 0;
        return hours * 60 + minutes;
    }, []);

    // Obter lista única de gestores
    const gestoresList = useMemo(() => {
        const gestores = new Set(apontamentos.map(apt => apt.gestor));
        return Array.from(gestores).sort();
    }, [apontamentos]);

    // Obter lista única de colaboradores (filtrada por gestor se selecionado)
    const colaboradoresList = useMemo(() => {
        let filtered = apontamentos;
        
        // Se um gestor foi selecionado, filtra apenas colaboradores daquele gestor
        if (selectedGestor) {
            filtered = apontamentos.filter(apt => apt.gestor === selectedGestor);
        }
        
        const colaboradores = new Set(filtered.map(apt => apt.nomeCompleto));
        return Array.from(colaboradores).sort();
    }, [apontamentos, selectedGestor]);

    // Limpar filtro de colaborador quando gestor muda
    useEffect(() => {
        setSelectedColaborador('');
    }, [selectedGestor]);

    // Filtrar dados
    const filteredData = useMemo(() => {
        return apontamentos.filter(item => {
            const matchGestor = selectedGestor ? item.gestor === selectedGestor : true;
            const matchColaborador = selectedColaborador ? item.nomeCompleto === selectedColaborador : true;
            return matchGestor && matchColaborador;
        });
    }, [apontamentos, selectedGestor, selectedColaborador]);

    // Ordenar dados
    const sortedData = useMemo(() => {
        if (!sortConfig.key) return filteredData;

        return [...filteredData].sort((a, b) => {
            let aValue = a[sortConfig.key];
            let bValue = b[sortConfig.key];

            // Para coluna de dia, usa o campo numérico
            if (sortConfig.key === 'dia') {
                aValue = a.diaOrdenacao;
                bValue = b.diaOrdenacao;
            }

            // Para colunas de tempo, converte para minutos
            if (['duracao', 'extra', 'extraExtraordinario', 'adicionalNoturno', 'entrada', 'pausa', 'retorno', 'saida'].includes(sortConfig.key)) {
                aValue = timeStringToMinutes(aValue);
                bValue = timeStringToMinutes(bValue);
            }

            // Para strings, normaliza
            if (typeof aValue === 'string' && typeof bValue === 'string') {
                aValue = aValue.toLowerCase();
                bValue = bValue.toLowerCase();
            }

            if (aValue < bValue) {
                return sortConfig.direction === 'asc' ? -1 : 1;
            }
            if (aValue > bValue) {
                return sortConfig.direction === 'asc' ? 1 : -1;
            }
            return 0;
        });
    }, [filteredData, sortConfig, timeStringToMinutes]);

    // Converter minutos para string HH:MM
    const minutesToTimeString = useCallback((minutes) => {
        const hours = Math.floor(minutes / 60);
        const mins = minutes % 60;
        return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
    }, []);

    // Ordenação
    const handleSort = useCallback((key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    }, [sortConfig]);

    // Componente de ícone de ordenação
    const SortIcon = ({ column }) => {
        if (sortConfig.key !== column) {
            return <span className="ml-1" style={{ opacity: 0.3 }}>⇅</span>;
        }
        return <span className="ml-1">{sortConfig.direction === 'asc' ? '▴' : '▾'}</span>;
    };

    // Ações do header
    const actions = useMemo(
        () => [
            {
                label: 'Voltar',
                icon: <MdOutlineArrowBack />,
                color: 'info',
                type: 'button',
                onClick: () => history.goBack()
            }
        ],
        [history]
    );

    // Cálculo de totais
    const totals = useMemo(() => {
        return {
            duracao: sortedData.reduce((sum, item) => sum + timeStringToMinutes(item.duracao), 0),
            extra: sortedData.reduce((sum, item) => sum + timeStringToMinutes(item.extra), 0),
            extraExtraordinario: sortedData.reduce((sum, item) => sum + timeStringToMinutes(item.extraExtraordinario), 0),
            adicionalNoturno: sortedData.reduce((sum, item) => sum + timeStringToMinutes(item.adicionalNoturno), 0)
        };
    }, [sortedData, timeStringToMinutes]);

    // Formatar data para título (suporta Date ou string)
    const formatarDataParaTitulo = (data) => {
        if (!data) return '';
        try {
            let dataObj;
            if (typeof data === 'string') {
                // Se for string DD/MM/YYYY, retorna como está
                if (data.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
                    return data;
                }
                // Se for string de data ISO ou outro formato, converte
                dataObj = new Date(data);
            } else if (data instanceof Date) {
                dataObj = data;
            } else {
                return data.toString();
            }
            
            const dia = String(dataObj.getDate()).padStart(2, '0');
            const mes = String(dataObj.getMonth() + 1).padStart(2, '0');
            const ano = dataObj.getFullYear();
            return `${dia}/${mes}/${ano}`;
        } catch {
            return data.toString();
        }
    };

    // Função auxiliar para extrair mês/ano
    const extrairMesAnoRelatorio = useCallback(() => {
        try {
            if (periodoFinal && typeof periodoFinal === 'string') {
                const [dia, mes, ano] = periodoFinal.split('/');
                return {
                    mesRelatorio: parseInt(mes, 10) - 1, // 0-11
                    anoRelatorio: parseInt(ano, 10)
                };
            } else if (periodoFinal instanceof Date) {
                return {
                    mesRelatorio: periodoFinal.getMonth(),
                    anoRelatorio: periodoFinal.getFullYear()
                };
            }
        } catch {
            return { mesRelatorio: null, anoRelatorio: null };
        }
        return { mesRelatorio: null, anoRelatorio: null };
    }, [periodoFinal]);

    // Determinar quais colunas devem ser exibidas baseado na regra de negócio
    const determinarColunasVisiveis = useMemo(() => {
        const hoje = new Date();
        const diaAtual = hoje.getDate();
        const mesAtual = hoje.getMonth(); // 0-11
        const anoAtual = hoje.getFullYear();

        // Extrair mês/ano do período do relatório (periodoFinal)
        const { mesRelatorio, anoRelatorio } = extrairMesAnoRelatorio();

        if (mesRelatorio === null || anoRelatorio === null) {
            // Padrão: mostrar todas as colunas
            return {
                horaExtra: true,
                horaExtraExtraordinaria: true,
                adicionalNoturno: true,
                situacao: true
            };
        }

        // Calcular diferença de meses
        const diferencaMeses = (anoAtual - anoRelatorio) * 12 + (mesAtual - mesRelatorio);

        // Regras de exibição
        if (diaAtual >= 1 && diaAtual <= 15) {
            // Dias 1 a 15 do mês atual
            if (diferencaMeses >= 2) {
                // Relatórios de meses até (mês atual - 2)
                return {
                    horaExtra: true,
                    horaExtraExtraordinaria: true,
                    adicionalNoturno: true,
                    situacao: true
                };
            } else {
                // Mês atual ou mês atual - 1
                return {
                    horaExtra: true,
                    horaExtraExtraordinaria: false,
                    adicionalNoturno: false,
                    situacao: false
                };
            }
        } else {
            // Dia 16 ou mais do mês atual
            if (diferencaMeses >= 2) {
                // Relatórios de meses até (mês atual - 1)
                return {
                    horaExtra: true,
                    horaExtraExtraordinaria: true,
                    adicionalNoturno: true,
                    situacao: true
                };
            } else {
                // Mês atual
                return {
                    horaExtra: true,
                    horaExtraExtraordinaria: false,
                    adicionalNoturno: false,
                    situacao: false
                };
            }
        }
    }, [extrairMesAnoRelatorio]);

    // Determinar mensagem do tooltip baseado na regra de negócio
    const mensagemTooltip = useMemo(() => {
        const hoje = new Date();
        const diaAtual = hoje.getDate();
        const mesAtual = hoje.getMonth(); // 0-11
        const anoAtual = hoje.getFullYear();

        const { mesRelatorio, anoRelatorio } = extrairMesAnoRelatorio();

        if (mesRelatorio === null || anoRelatorio === null) {
            return "Relatório de horas";
        }

        // Calcular diferença de meses
        const diferencaMeses = (anoAtual - anoRelatorio) * 12 + (mesAtual - mesRelatorio);

        if (diaAtual >= 1 && diaAtual <= 15) {
            // Dias 1 a 15 do mês atual
            if (diferencaMeses >= 2) {
                // Relatórios de meses até (mês atual - 2)
                return "Relatório de horas aprovadas pelo DP";
            } else if (diferencaMeses === 1) {
                // Mês atual - 1
                return "Relatório de horas em aprovação do DP";
            } else {
                // Mês atual
                return "Relatório de horas em processamento";
            }
        } else {
            // Dia 16 ou mais do mês atual
            if (diferencaMeses >= 2) {
                // Relatórios de meses até (mês atual - 1)
                return "Relatório de horas aprovadas pelo DP";
            } else {
                // Mês atual
                return "Relatório de horas em processamento";
            }
        }
    }, [extrairMesAnoRelatorio]);


    const styleColunaSortavel = {
        cursor: 'pointer',
        userSelect: 'none',
        backgroundColor: '#f5f5f5',
        fontWeight: '500'
    };

    const styleTotalRow = {
        backgroundColor: '#FFFF99',
        fontWeight: 'bold'
    };

    const styleGreenColumn = {
        backgroundColor: '#CCFFCC'
    };

    const tituloString = `Relatório de Horas no Ponto - ${formatarDataParaTitulo(periodoInicial)} a ${formatarDataParaTitulo(periodoFinal)}`;

    return (
        <PageContainer title={tituloString} actions={actions}>
            {loading ? (
                <Loading message="Carregando relatório..." />
            ) : error ? (
                <Alert variant="danger" className="mb-3">{error}</Alert>
            ) : (
                <>
                    <div className="alert alert-warning d-flex align-items-center" role="alert">
                        <div style={{width: "50px"}}>
                            <IoIosWarning size={20}/>
                        </div>
        <div>
                            {mensagemTooltip}
                        </div>
                    </div>
                    <div className="mb-3">
                        <div className="d-flex gap-3 mb-3">
                            <Button onClick={() => exportDataInExcel()} className="mr-3" disabled={dadosOriginais.length === 0}>
                                Exportar Todos
                            </Button>

                            <Dropdown className="mr-3">
                                <Dropdown.Toggle variant="light" id="dd-gestor">
                                    Filtrar por Gestor: <span style={{ fontWeight: 'bold' }}>{selectedGestor ? selectedGestor : "Todos"}</span>
                                </Dropdown.Toggle>
                                <Dropdown.Menu>
                                    <Dropdown.Item onClick={() => setSelectedGestor('')}>
                                        Todos
                                    </Dropdown.Item>
                                    {gestoresList.map(gestor => (
                                        <Dropdown.Item key={gestor} onClick={() => setSelectedGestor(gestor)}>
                                            {gestor}
                                        </Dropdown.Item>
                                    ))}
                                </Dropdown.Menu>
                            </Dropdown>

                            <Dropdown>
                                <Dropdown.Toggle variant="light" id="dd-colaborador">
                                    Filtrar por Colaborador: <span style={{ fontWeight: 'bold' }}>{selectedColaborador ? selectedColaborador.substring(0, 20) + (selectedColaborador.length > 20 ? '...' : '') : "Todos"}</span>
                                </Dropdown.Toggle>
                                <Dropdown.Menu>
                                    <Dropdown.Item onClick={() => setSelectedColaborador('')}>
                                        Todos
                                    </Dropdown.Item>
                                    {colaboradoresList.map(colab => (
                                        <Dropdown.Item key={colab} onClick={() => setSelectedColaborador(colab)}>
                                            {colab}
                                        </Dropdown.Item>
                                    ))}
                                </Dropdown.Menu>
                            </Dropdown>
                        </div>
                    </div>

                    {sortedData.length === 0 ? (
                        <Alert variant="warning">
                            Nenhum dado encontrado para o período e filtros selecionados.
                        </Alert>
                    ) : (
                        <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: 'calc(100vh - 300px)' }}>
                            <Table striped bordered hover responsive="lg">
                                <thead style={{ position: 'sticky', top: 0, backgroundColor: '#f5f5f5', zIndex: 10 }}>
                                    <tr>
                                        <th style={styleColunaSortavel} onClick={() => handleSort('dia')}>
                                            Dia <SortIcon column="dia" />
                                        </th>
                                        <th style={styleColunaSortavel} onClick={() => handleSort('nomeCompleto')}>
                                            Nome <SortIcon column="nomeCompleto" />
                                        </th>
                                        <th style={styleColunaSortavel} onClick={() => handleSort('gestor')}>
                                            Gestor <SortIcon column="gestor" />
                                        </th>
                                        <th style={styleColunaSortavel} onClick={() => handleSort('entrada')}>
                                            Entrada <SortIcon column="entrada" />
                                        </th>
                                        <th style={styleColunaSortavel} onClick={() => handleSort('pausa')}>
                                            Pausa <SortIcon column="pausa" />
                                        </th>
                                        <th style={styleColunaSortavel} onClick={() => handleSort('retorno')}>
                                            Retorno <SortIcon column="retorno" />
                                        </th>
                                        <th style={styleColunaSortavel} onClick={() => handleSort('saida')}>
                                            Saída <SortIcon column="saida" />
                                        </th>
                                        <th style={{ ...styleColunaSortavel, ...styleGreenColumn }} onClick={() => handleSort('duracao')}>
                                            Duração <SortIcon column="duracao" />
                                        </th>
                                        {determinarColunasVisiveis.horaExtra && (
                                            <th style={{ ...styleColunaSortavel, ...styleGreenColumn }} onClick={() => handleSort('extra')}>
                                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '6px' }}>
                                                    <span>Hora Extra / Banco de Horas</span>
                                                    <OverlayTrigger
                                                        placement="bottom"
                                                        overlay={
                                                            <Tooltip id="tooltip-hora-extra">
                                                                Contém o banco de horas, podendo conter também horas extras 50% se existentes
                                                            </Tooltip>
                                                        }
                                                    >
                                                        <span style={{ cursor: 'help' }}>
                                                            <FiInfo size={16} style={{ color: '#212529' }} />
                                                        </span>
                                                    </OverlayTrigger>
                                                </div>
                                                <SortIcon column="extra" />
                                            </th>
                                        )}
                                        {determinarColunasVisiveis.horaExtraExtraordinaria && (
                                            <th style={{ ...styleColunaSortavel, ...styleGreenColumn }} onClick={() => handleSort('extraExtraordinario')}>
                                                Hora Extra Extraordinária <SortIcon column="extraExtraordinario" />
                                            </th>
                                        )}
                                        {determinarColunasVisiveis.adicionalNoturno && (
                                            <th style={{ ...styleColunaSortavel, ...styleGreenColumn }} onClick={() => handleSort('adicionalNoturno')}>
                                                Adicional Noturno <SortIcon column="adicionalNoturno" />
                                            </th>
                                        )}
                                    </tr>
                                </thead>
                                <tbody>
                                    {sortedData.map((item, index) => (
                                        <tr key={item.id || index}>
                                            <td>{item.dia}</td>
                                            <td>{item.nomeCompleto}</td>
                                            <td>{item.gestor}</td>
                                            <td className="text-center">{item.entrada}</td>
                                            <td className="text-center">{item.pausa}</td>
                                            <td className="text-center">{item.retorno}</td>
                                            <td className="text-center">{item.saida}</td>
                                            <td className="text-center" style={styleGreenColumn}>{item.duracao}</td>
                                            {determinarColunasVisiveis.horaExtra && (
                                                <td className="text-center" style={styleGreenColumn}>{item.extra}</td>
                                            )}
                                            {determinarColunasVisiveis.horaExtraExtraordinaria && (
                                                <td className="text-center" style={styleGreenColumn}>{item.extraExtraordinario}</td>
                                            )}
                                            {determinarColunasVisiveis.adicionalNoturno && (
                                                <td className="text-center" style={styleGreenColumn}>{item.adicionalNoturno}</td>
                                            )}
                                        </tr>
                                    ))}
                                    <tr style={styleTotalRow}>
                                        <td colSpan="7"><strong>TOTAL</strong></td>
                                        <td className="text-center" style={styleGreenColumn}><strong>{minutesToTimeString(totals.duracao)}</strong></td>
                                        {determinarColunasVisiveis.horaExtra && (
                                            <td className="text-center" style={styleGreenColumn}><strong>{minutesToTimeString(totals.extra)}</strong></td>
                                        )}
                                        {determinarColunasVisiveis.horaExtraExtraordinaria && (
                                            <td className="text-center" style={styleGreenColumn}><strong>{minutesToTimeString(totals.extraExtraordinario)}</strong></td>
                                        )}
                                        {determinarColunasVisiveis.adicionalNoturno && (
                                            <td className="text-center" style={styleGreenColumn}><strong>{minutesToTimeString(totals.adicionalNoturno)}</strong></td>
                                        )}
                                        {determinarColunasVisiveis.situacao && (
                                            <td className="text-center"></td>
                                        )}
                                    </tr>
                                </tbody>
                            </Table>
        </div>
                    )}
                </>
            )}
        </PageContainer>
    );
}
export default RelatorioRelacaoHorasView;